'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useCommonData } from '@udemy-v2/common-data';
import { LocalizedHtml, useI18n } from '@udemy-v2/i18n';
import { Button } from '@udemy-v2/react-core-components';
import { FooterButtons, Modal } from '@udemy-v2/react-dialog-components';
import { FormGroup, TextInput } from '@udemy-v2/react-form-components';
import { AlertBanner } from '@udemy-v2/react-notice-components';
import { buildSignupPopupUrl } from '@/app/lib/auth/signup-popup-url';
import { CourseAccessQueryParam } from './constants';
import { CourseLimitedAccess } from './course-limited-access.react-component';
import styles from './password-protected-course.module.scss';
import classNames from 'classnames';
interface CoursePasswordFormProps {
  courseTitle?: string;
  error?: string;
  /**
   * Query params from the current URL (e.g. `referralCode`) to carry through the
   * GET submission. A GET form replaces the whole query string, so without these
   * the submitted URL would only contain `password`.
   */
  preservedSearchParams?: [string, string][];
}

/**
 * Form component for entering a course password in a modal. Displays an optional error message and a heading.
 */
export function CoursePasswordForm(t0) {
  const $ = _c(28);
  const {
    courseTitle,
    error,
    preservedSearchParams: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const preservedSearchParams = t2;
  const {
    gettext,
    interpolate
  } = useI18n();
  let t3;
  if ($[2] !== courseTitle || $[3] !== gettext || $[4] !== interpolate) {
    t3 = courseTitle ? <LocalizedHtml html={interpolate(gettext("Enter password for: <em class=\"title\">%(title)s</em>"), {
      title: courseTitle
    }, true)} interpolate={{
      title: <em />
    }} /> : gettext("Enter password");
    $[2] = courseTitle;
    $[3] = gettext;
    $[4] = interpolate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const passwordLabel = t3;
  let t4;
  if ($[6] !== preservedSearchParams) {
    t4 = preservedSearchParams.map(_temp);
    $[6] = preservedSearchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames("ud-heading-lg", styles.title);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== gettext) {
    t6 = gettext("Course password");
    $[9] = gettext;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <h2 id="course-password-title" className={t5}>{t6}</h2>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== error) {
    t8 = error && <AlertBanner udStyle="error" title={error} showCta={false} className={styles["alert-banner"]} />;
    $[13] = error;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <TextInput type="password" name="password" autoComplete="current-password" />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== passwordLabel) {
    t10 = <FormGroup label={passwordLabel} className={styles["form-group"]}>{t9}</FormGroup>;
    $[16] = passwordLabel;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== gettext) {
    t11 = gettext("View course");
    $[18] = gettext;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <FooterButtons><Button type="submit">{t11}</Button></FooterButtons>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12 || $[24] !== t4 || $[25] !== t7 || $[26] !== t8) {
    t13 = <form method="get">{t4}{t7}{t8}{t10}{t12}</form>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t4;
    $[25] = t7;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
}
function _temp(t0, index) {
  const [name, value] = t0;
  return <input key={`${name}-${index}`} type="hidden" name={name} value={value} />;
}
interface PasswordProtectedCourseProps {
  courseTitle?: string;
  error?: string;
}
export function PasswordProtectedCourse(t0) {
  const $ = _c(21);
  const {
    courseTitle,
    error
  } = t0;
  const {
    gettext,
    locale
  } = useI18n();
  const commonData = useCommonData();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(!!error);
  const isLoggedIn = !!commonData?.user;
  let t1;
  if ($[0] !== searchParams) {
    t1 = Array.from(searchParams?.entries() ?? []).filter(_temp2);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const preservedSearchParams = t1;
  let t2;
  if ($[2] !== isLoggedIn || $[3] !== locale || $[4] !== pathname || $[5] !== searchParams) {
    t2 = function handleButtonClick() {
      if (!isLoggedIn) {
        window.location.assign(buildSignupPopupUrl({
          locale,
          nextPath: pathname ?? "/",
          searchParams: searchParams?.entries()
        }));
        return;
      }
      setIsModalOpen(true);
    };
    $[2] = isLoggedIn;
    $[3] = locale;
    $[4] = pathname;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleButtonClick = t2;
  let t3;
  if ($[7] !== gettext) {
    t3 = gettext("Enter password");
    $[7] = gettext;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsModalOpen(false);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== courseTitle || $[11] !== error || $[12] !== preservedSearchParams) {
    t5 = <CoursePasswordForm courseTitle={courseTitle} error={error} preservedSearchParams={preservedSearchParams} />;
    $[10] = courseTitle;
    $[11] = error;
    $[12] = preservedSearchParams;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== isModalOpen || $[15] !== t5) {
    t6 = <Modal title="" renderTitle={_temp3} isOpen={isModalOpen} onClose={t4}>{t5}</Modal>;
    $[14] = isModalOpen;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== handleButtonClick || $[18] !== t3 || $[19] !== t6) {
    t7 = <CourseLimitedAccess buttonLabel={t3} onButtonClick={handleButtonClick}>{t6}</CourseLimitedAccess>;
    $[17] = handleButtonClick;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function _temp3() {
  return ["course-password-title", null];
}
function _temp2(t0) {
  const [name] = t0;
  return name !== CourseAccessQueryParam.PASSWORD;
}